import { validateTimeFields } from '../../../../common/timeFormat';
/* eslint react/prop-types: 0 */
import React, { useState } from 'react';
import { useFormik } from 'formik';
import { validatePercentageFields } from '../../../../common/percentageFormat';

import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import DataView from '../../../components/DataView';
import { useFormData } from '../../../hooks/use-form-data';
import { updateValues } from '../../../services/api/update-fields.api';
import { AUTOCLOSE_ALERT_DELAY } from '../../../shared/constants/snackbar.constants';

/**
 * @param {{
 * fieldsData: Array<import("../../../hooks/use-form-data").FieldsData>
 * tabId: string;
 * selectedId: string;
 * objectType: "party" | "service" | "contract";
 * showLoader: VoidFunction;
 * onValuesSaved?: (values: Record<string, unknown>) => void;
 * }} props
 */
const TabDetails = ({
  fieldsData,
  tabId,
  selectedId,
  objectType,
  showLoader,
  onValuesSaved
}) => {
  const defaultAlertData = { type: '', message: '' };
  const [showAlert, setShowAlert] = useState(false);
  const [alertData, setAlertData] = useState(defaultAlertData);
  const [isEditMode, setIsEditMode] = useState(false);
  const { formData, setFormData } = useFormData(fieldsData);

  const discardChanges = () => {
    setIsEditMode(false);
  };

  const saveChanges = async (values) => {
    const payload = { values: [] };

    for (const fieldId of Object.keys(values)) {
      payload.values.push({
        field_id: fieldId,
        subject_type: 'asset',
        object_type: objectType,
        subject_id: tabId,
        object_id: selectedId,
        value: Array.isArray(values[fieldId])
          ? values[fieldId].join(',')
          : values[fieldId]
      });
    }

    try {
      showLoader(true);
      await updateValues(payload);
      onValuesSaved?.(
        Object.fromEntries(
          payload.values.map(({ field_id: fieldId, value }) => [fieldId, value])
        )
      );
      setFormData({ ...values });
      setIsEditMode(false);
      setShowAlert(true);
      setAlertData({ type: 'success', message: 'Successfully saved!' });
    } catch (err) {
      setShowAlert(true);
      setAlertData({
        type: 'error',
        message: 'Failed to save. Please, try again later.'
      });
    } finally {
      showLoader(false);
    }
  };

  const formik = useFormik({
    initialValues: { ...formData },
    enableReinitialize: true,
    validate: (values) => ({
      ...validatePercentageFields(fieldsData, values),
      ...validateTimeFields(fieldsData, values)
    }),
    onSubmit: saveChanges
  });

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  const closeAlert = () => {
    setShowAlert(false);
    setAlertData(defaultAlertData);
  };

  const iconButtonClasses = ['edit-button', isEditMode ? 'active' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className="tab-edit-form">
      <IconButton
        aria-label="Edit details"
        className={iconButtonClasses}
        type="button"
        disabled={false}
        onClick={toggleEditMode}
      >
        <EditIcon />
      </IconButton>

      <form
        onSubmit={formik.handleSubmit}
        onReset={(event) => {
          discardChanges();
          formik.handleReset(event);
        }}
      >
        {fieldsData.map((card, idx) => {
          const key = `${card.title}-${idx}`;
          return (
            <DataView
              key={key}
              orientation="horizontal"
              card={card}
              isEdit={isEditMode}
              values={formik.values}
              handleChange={formik.handleChange}
              handleBlur={formik.handleBlur}
            />
          );
        })}

        {isEditMode ? (
          <div className="form-actions">
            <Button type="reset">Cancel</Button>
            <Button type="submit">Save</Button>
          </div>
        ) : null}
      </form>

      <Snackbar
        open={showAlert}
        autoHideDuration={AUTOCLOSE_ALERT_DELAY}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={closeAlert}
      >
        <Alert severity={alertData.type === 'success' ? 'success' : 'error'}>
          {alertData.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default TabDetails;
